import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Flag, BookOpen, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutPage = () => {
  const credentials = [
    { icon: Award, title: 'Certified Golf Psychology Coach' },
    { icon: BookOpen, title: 'Graduate Certificate in Sports Coaching - University of Canterbury, NZ' },
    { icon: Award, title: 'Certified Mind Factor Coach' },
    { icon: Award, title: 'Certified Fluid Motion Factor Coach' },
    { icon: Award, title: 'Certified FocusBand Instructor' },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#2C5530]" data-testid="about-hero">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-white/70 text-sm uppercase tracking-[0.2em] mb-4">About</p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl text-white font-medium mb-6">
              Scott Ritchie
            </h1>
            <p className="text-white/80 text-xl leading-relaxed">
              Golf psychology coach and professional caddy helping golfers master their mental game since 2012.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 md:py-32" data-testid="about-content">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Image */}
            <div className="lg:sticky lg:top-32">
              <img
                src="https://images.pexels.com/photos/6256590/pexels-photo-6256590.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Golf coaching session"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="bg-[#F2F0E9] p-6 mt-6 flex items-center gap-4">
                <Flag className="w-8 h-8 text-[#2C5530]" />
                <div>
                  <p className="font-semibold text-[#1A1A1A]">International Coaching</p>
                  <p className="text-[#57534E] text-sm">Available worldwide, in-person and online</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-8">
                Experience at the Highest Level
              </h2>
              
              <div className="prose max-w-none">
                <p className="text-[#57534E] text-lg leading-relaxed mb-6">
                  I have been working with golfers on their mental performance since 2012, combining formal education with 
                  real-world experience at the highest levels of the game.
                </p>
                
                <p className="text-[#57534E] leading-relaxed mb-6">
                  As a professional caddy since 2002, I have been fortunate to work alongside some of the top-ranked players 
                  in the world. This unique perspective has given me invaluable insights into how elite performers handle 
                  pressure, manage their mental states, and execute under the most demanding circumstances.
                </p>

                <p className="text-[#57534E] leading-relaxed mb-8">
                  My approach combines psychological principles with the practical reality of tournament play. I've seen 
                  firsthand what works under pressure — and more importantly, what doesn't. This experience informs every 
                  aspect of my coaching.
                </p>
              </div>

              {/* Credentials */}
              <div className="mb-12">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6">Qualifications</h3>
                <div className="space-y-4">
                  {credentials.map((cred, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-4 p-4 bg-white border border-[#E7E5E4]"
                      data-testid={`credential-${index}`}
                    >
                      <cred.icon className="w-5 h-5 text-[#2C5530] mt-0.5 flex-shrink-0" />
                      <span className="text-[#57534E]">{cred.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Philosophy */}
              <div className="bg-[#2C5530] p-8 md:p-10 text-white mb-12">
                <h3 className="font-['Playfair_Display'] text-2xl mb-4">My Philosophy</h3>
                <p className="text-white/80 leading-relaxed">
                  Quiet Mind Golf is about boosting your mental game with practical tools. I welcome everyone, no matter 
                  your skill level, who wants to understand themselves better and perform at their best. The goal is to 
                  enhance your performance, allowing you to stay on track with your goals even when facing internal challenges.
                </p>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-[#BC562B] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#9A4520] transition-all duration-300 flex items-center justify-center gap-2"
                  data-testid="about-cta-btn"
                >
                  Work With Me
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/blog"
                  className="border border-[#2C5530] text-[#2C5530] px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#2C5530] hover:text-white transition-all duration-300 text-center"
                  data-testid="about-blog-btn"
                >
                  Read the Blog
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Caddy Section */}
      <section className="py-24 md:py-32 bg-[#F2F0E9]" data-testid="caddy-section">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Users className="w-12 h-12 text-[#2C5530] mb-6" />
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#1A1A1A] mb-6">
                Insights from Inside the Ropes
              </h2>
              <p className="text-[#57534E] text-lg leading-relaxed mb-6">
                My continued work as a professional caddy keeps me connected to the realities of competitive golf. 
                I see daily how mental states affect performance, how pressure manifests, and what strategies actually 
                work when it matters most.
              </p>
              <p className="text-[#57534E] leading-relaxed">
                This isn't theory from a textbook — it's knowledge earned on fairways and greens around the world, 
                in professional tournaments and working with amateurs at all levels.
              </p>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1718348635597-17cc8022763e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxzdCUyMGFuZHJld3MlMjBnb2xmJTIwY291cnNlJTIwbGFuZHNjYXBlJTIwc2NvdGxhbmR8ZW58MHx8fHwxNzc0MjIxODUxfDA&ixlib=rb-4.1.0&q=85"
                alt="St Andrews golf course"
                className="w-full aspect-[16/10] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
